import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import Pagination from "@mui/material/Pagination";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";

import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import CentroEquipoDetalleDialog from "../../requerimiento/components/CentroEquipoDetalleDialog";
import GrupoFormDialog from "./GrupoFormDialog";
import {
  getGrupoDescripcion,
  getGrupoId,
  getGrupoNombre,
  getGrupoUnitIds,
  getGrupoUsuarios,
  getUnidadId,
  getUnidadNombre,
  getUnidadUid,
} from "../utils/plataformaGrupoHelpers";

const limitOptions = [10, 25, 50, 100, 200];
const tableHeadBg = (theme) =>
  theme.palette.mode === "dark" ? theme.palette.background.paper : theme.palette.grey[100];

const pad2 = (n) => String(n).padStart(2, "0");

const formatDateTime = (value) => {
  if (!value) return "-";
  let date = null;

  if (typeof value === "number") {
    date = new Date(value < 100000000000 ? value * 1000 : value);
  } else {
    const numeric = Number(value);
    if (!Number.isNaN(numeric) && String(value).trim() !== "") {
      date = new Date(numeric < 100000000000 ? numeric * 1000 : numeric);
    } else {
      date = new Date(value);
    }
  }

  if (!date || Number.isNaN(date.getTime())) return String(value);
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())} ${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`;
};

const getParamValue = (obj, key) => {
  const raw = obj?.[key];
  if (raw && typeof raw === "object" && "v" in raw) return raw.v;
  return raw;
};

const getUnidadPosition = (u) => {
  const prms = u?.prms || u?.params || {};
  const pos = u?.pos || u?.lmsg?.pos || u?.message?.pos || {};
  const lat = pos.y ?? pos.lat ?? u?.["pos.y"] ?? getParamValue(prms, "pos.y");
  const lng = pos.x ?? pos.lon ?? pos.lng ?? u?.["pos.x"] ?? getParamValue(prms, "pos.x");

  const latNum = Number(lat);
  const lngNum = Number(lng);
  if (Number.isNaN(latNum) || Number.isNaN(lngNum)) return null;
  if (latNum === 0 && lngNum === 0) return null;
  return { lat: latNum, lng: lngNum };
};

const getUnidadLastTime = (u) => {
  const prms = u?.prms || u?.params || {};
  return (
    u?.pos?.t ||
    u?.lmsg?.t ||
    u?.lmsg?.pos?.t ||
    u?.message?.t ||
    u?.tm ||
    u?.last_message_time ||
    getParamValue(prms, "pos.t") ||
    getParamValue(prms, "last_message_time")
  );
};

const getUnidadPreviewLabel = (unidad, fallbackId) => {
  if (unidad) return getUnidadNombre(unidad);
  return fallbackId;
};

const parseJsonSafe = (value) => {
  if (!value) return null;
  if (typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const diffList = (before = [], after = []) => {
  const b = new Set((before || []).map((item) => String(typeof item === "object" ? item.id : item)));
  const a = new Set((after || []).map((item) => String(typeof item === "object" ? item.id : item)));
  return {
    added: [...a].filter((id) => !b.has(id)),
    removed: [...b].filter((id) => !a.has(id)),
  };
};

const renderHistorySummary = (row) => {
  const before = parseJsonSafe(row.valores_anteriores) || {};
  const after = parseJsonSafe(row.valores_nuevos) || {};
  const parts = [];

  if ((before.nombre || "") !== (after.nombre || "")) {
    parts.push(`Nombre: ${before.nombre || "-"} -> ${after.nombre || "-"}`);
  }

  const unidades = diffList(before.unidades, after.unidades);
  if (unidades.added.length || unidades.removed.length) {
    parts.push(`Unidades +${unidades.added.length} / -${unidades.removed.length}`);
  }

  const usuarios = diffList(before.usuarios, after.usuarios);
  if (usuarios.added.length || usuarios.removed.length) {
    parts.push(`Usuarios +${usuarios.added.length} / -${usuarios.removed.length}`);
  }

  if (!parts.length && row.accion === "CREAR") {
    parts.push(`Creado con ${(after.unidades || []).length} unidades y ${(after.usuarios || []).length} usuarios`);
  }

  return parts.length ? parts.join(" | ") : "-";
};

export default function PlataformaGruposPage({
  title,
  subtitle,
  listGroups,
  createGroup,
  updateGroup,
  getGroupHistory,
  listUsers,
  listUnits,
  providerColor = "#006d75",
}) {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [limit, setLimit] = useState(25);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [openGroups, setOpenGroups] = useState({});
  const [error, setError] = useState("");
  // { open, mode: "create" | "edit", grupo } — un solo dialogo reusable
  // (GrupoFormDialog) para registrar y editar grupos.
  const [formDialog, setFormDialog] = useState({ open: false, mode: "create", grupo: null });
  const [equipoDialog, setEquipoDialog] = useState({ open: false, identificador: "" });
  const [infoDialog, setInfoDialog] = useState({ open: false, grupo: null });
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);

  const fetchRows = async ({ usePage = page, useSearch = appliedSearch, useLimit = limit } = {}) => {
    if (!listGroups) return;
    setLoading(true);
    setError("");

    try {
      const resp = await listGroups({ search: useSearch, page: usePage, limit: useLimit });
      const payload = resp?.data || {};
      const data = Array.isArray(payload.data) ? payload.data : [];
      setRows(data);
      setTotal(Number(payload.total || data.length || 0));
      setTotalPages(Number(payload.totalPages || 1));
    } catch (e) {
      setRows([]);
      setTotal(0);
      setTotalPages(1);
      setError(e?.response?.data?.error || e?.response?.data?.message || e.message || "No se pudieron cargar los grupos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]);

  const handleBuscar = () => {
    const value = search.trim();
    setAppliedSearch(value);
    setPage(1);
    fetchRows({ usePage: 1, useSearch: value, useLimit: limit });
  };

  const totals = useMemo(() => {
    const unidades = rows.reduce((acc, grupo) => acc + getGrupoUnitIds(grupo).length, 0);
    return { grupos: rows.length, unidades };
  }, [rows]);

  const toggleGroup = (grupo) => {
    const id = String(getGrupoId(grupo) || getGrupoNombre(grupo));
    setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const openCreateDialog = () => {
    setFormDialog({ open: true, mode: "create", grupo: null });
  };

  const openEditDialog = (grupo) => {
    setFormDialog({ open: true, mode: "edit", grupo });
  };

  const closeFormDialog = () => {
    setFormDialog((prev) => ({ ...prev, open: false }));
  };

  const handleFormDialogSaved = async () => {
    const wasCreate = formDialog.mode === "create";
    closeFormDialog();
    if (wasCreate) {
      setPage(1);
      await fetchRows({ usePage: 1, useSearch: appliedSearch, useLimit: limit });
    } else {
      await fetchRows({ usePage: page, useSearch: appliedSearch, useLimit: limit });
    }
  };

  const loadGroupHistory = async (grupo, usePage = 1) => {
    if (!getGroupHistory || !grupo) return;
    const itemId = getGrupoId(grupo);
    if (!itemId) return;

    setHistoryLoading(true);
    try {
      const resp = await getGroupHistory(itemId, { page: usePage, limit: 30 });
      const payload = resp?.data || {};
      setHistoryRows(Array.isArray(payload.data) ? payload.data : []);
      setHistoryPage(Number(payload.page || usePage || 1));
      setHistoryTotalPages(Number(payload.totalPages || 1));
    } catch (e) {
      setHistoryRows([]);
      setHistoryTotalPages(1);
      setError(e?.response?.data?.error || e?.response?.data?.message || e.message || "No se pudo cargar el historial del grupo");
    } finally {
      setHistoryLoading(false);
    }
  };

  const openInfoDialog = async (grupo) => {
    setInfoDialog({ open: true, grupo });
    await loadGroupHistory(grupo, 1);
  };

  const handleHistoryPageChange = (nextPage) => {
    loadGroupHistory(infoDialog.grupo, nextPage);
  };

  return (
    <DashboardLayout>
      <Box sx={{ p: { xs: 1.25, md: 2 }, display: "grid", gap: 1 }}>
        <PageHeader title={title} subtitle={subtitle} backTo=".." sx={{ mb: 1, borderRadius: 1 }} />

        <Paper
          variant="outlined"
          sx={{
            p: 0.75,
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            flexWrap: "wrap",
          }}
        >
          <TextField
            size="small"
            label="Buscar grupo"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") handleBuscar();
            }}
            sx={{ minWidth: 260, "& .MuiInputBase-input": { py: 0.75, fontSize: "0.82rem" } }}
          />

          <TextField
            select
            size="small"
            label="Limite"
            value={limit}
            onChange={(event) => {
              setLimit(Number(event.target.value));
              setPage(1);
            }}
            sx={{ width: 105, "& .MuiInputBase-input": { py: 0.75, fontSize: "0.82rem" } }}
          >
            {limitOptions.map((n) => (
              <MenuItem key={n} value={n}>{n}</MenuItem>
            ))}
          </TextField>

          <LoadingButton
            loading={loading}
            size="small"
            variant="contained"
            disableElevation
            startIcon={<SearchIcon />}
            onClick={handleBuscar}
            sx={{ bgcolor: providerColor, "&:hover": { bgcolor: providerColor } }}
          >
            Buscar
          </LoadingButton>

          <Button size="small" disabled={loading} startIcon={<RefreshIcon />} onClick={() => fetchRows()}>
            Actualizar
          </Button>

          {createGroup && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              disabled={loading}
              onClick={openCreateDialog}
              sx={{
                borderColor: providerColor,
                color: providerColor,
                textTransform: "none",
                fontWeight: 800,
              }}
            >
              Registrar grupo
            </Button>
          )}

          <Chip size="small" label={`Total: ${total}`} />
          <Chip size="small" label={`Pagina: ${totals.grupos}`} />
          <Chip size="small" label={`Unidades pagina: ${totals.unidades}`} />
        </Paper>

        {!!error && (
          <Paper variant="outlined" sx={{ p: 1, color: "error.main", fontSize: "0.82rem" }}>
            {error}
          </Paper>
        )}

        <Paper variant="outlined" sx={{ opacity: loading ? 0.65 : 1 }}>
          <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 820, "& .MuiTableCell-root": { fontSize: "0.78rem", py: 0.65, px: 0.8 } }}>
              <TableHead>
                <TableRow sx={{ "& th": { bgcolor: tableHeadBg, fontWeight: 900 } }}>
                  <TableCell>ID</TableCell>
                  <TableCell>Grupo</TableCell>
                  <TableCell>Descripcion</TableCell>
                  <TableCell align="center">Unidades</TableCell>
                  <TableCell align="center">Usuarios</TableCell>
                  <TableCell>Primeras unidades</TableCell>
                  <TableCell align="right">Detalle</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={22} />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3, color: "text.secondary" }}>
                      No hay grupos para mostrar.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((grupo) => {
                    const units = getGrupoUnitIds(grupo);
                    const usuariosGrupo = getGrupoUsuarios(grupo);
                    const unidadesDetalle = Array.isArray(grupo?.unidades_detalle) ? grupo.unidades_detalle : [];
                    const groupKey = String(getGrupoId(grupo) || getGrupoNombre(grupo));
                    const open = !!openGroups[groupKey];
                    return (
                      <React.Fragment key={groupKey}>
                        <TableRow hover>
                          <TableCell>{getGrupoId(grupo) || "-"}</TableCell>
                          <TableCell sx={{ fontWeight: 900 }}>{getGrupoNombre(grupo)}</TableCell>
                          <TableCell>{getGrupoDescripcion(grupo) || "-"}</TableCell>
                          <TableCell align="center">
                            <Chip size="small" label={units.length} sx={{ fontSize: "0.7rem" }} />
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title={usuariosGrupo.map((u) => u?.nm || u?.name || u?.id).filter(Boolean).join(", ") || "Sin usuarios detectados"}>
                              <Chip size="small" label={usuariosGrupo.length} sx={{ fontSize: "0.7rem" }} />
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", gap: 0.4, flexWrap: "wrap" }}>
                              {units.slice(0, 6).map((id) => {
                                const unidad = unidadesDetalle.find((item) => String(getUnidadId(item)) === String(id));
                                return (
                                  <Chip
                                    key={id}
                                    size="small"
                                    label={getUnidadPreviewLabel(unidad, id)}
                                    title={unidad ? `ID ${id}` : ""}
                                    sx={{ fontSize: "0.66rem", height: 20, maxWidth: 150 }}
                                  />
                                );
                              })}
                              {units.length > 6 && <Chip size="small" label={`+${units.length - 6}`} sx={{ fontSize: "0.66rem", height: 20 }} />}
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            {updateGroup && (
                              <Tooltip title="Editar grupo">
                                <span>
                                  <IconButton size="small" onClick={() => openEditDialog(grupo)} disabled={loading}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            {getGroupHistory && (
                              <Tooltip title="Ver informacion del grupo">
                                <span>
                                  <IconButton size="small" onClick={() => openInfoDialog(grupo)} disabled={loading}>
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            <Tooltip title={open ? "Ocultar unidades" : "Mostrar unidades"}>
                              <span>
                                <IconButton size="small" onClick={() => toggleGroup(grupo)} disabled={!units.length}>
                                  {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={7} sx={{ p: 0, borderBottom: open ? "1px solid #e5e7eb" : 0 }}>
                            <Collapse in={open} timeout="auto" unmountOnExit>
                              <Box sx={{ px: 1.25, py: 1, bgcolor: (theme) => theme.palette.action.hover }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
                                  Unidades del grupo ({units.length})
                                </Typography>
                                <Table size="small" sx={{ "& .MuiTableCell-root": { fontSize: "0.74rem", py: 0.55, px: 0.7 } }}>
                                  <TableHead>
                                    <TableRow sx={{ "& th": { fontWeight: 900, bgcolor: tableHeadBg } }}>
                                      <TableCell>ID</TableCell>
                                      <TableCell>Unidad</TableCell>
                                      <TableCell>UID</TableCell>
                                      <TableCell>Fecha reporte</TableCell>
                                      <TableCell>Coordenadas</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {unidadesDetalle.length > 0 ? (
                                      unidadesDetalle.map((unidad) => {
                                        const position = getUnidadPosition(unidad);
                                        return (
                                          <TableRow key={unidad?.id || unidad?.uid || unidad?.nm}>
                                            <TableCell>{unidad?.id || "-"}</TableCell>
                                            <TableCell sx={{ fontWeight: 800 }}>{getUnidadNombre(unidad)}</TableCell>
                                            <TableCell>
                                              {getUnidadUid(unidad) !== "-" ? (
                                                <Button
                                                  size="small"
                                                  variant="text"
                                                  onClick={() => setEquipoDialog({ open: true, identificador: getUnidadUid(unidad) })}
                                                  sx={{
                                                    minWidth: 0,
                                                    p: 0,
                                                    fontSize: "0.72rem",
                                                    fontWeight: 900,
                                                    color: providerColor,
                                                    textTransform: "none",
                                                  }}
                                                >
                                                  {getUnidadUid(unidad)}
                                                </Button>
                                              ) : "-"}
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDateTime(getUnidadLastTime(unidad))}</TableCell>
                                            <TableCell>
                                              {position ? (
                                                <Button
                                                  size="small"
                                                  href={`https://www.google.com/maps?q=${position.lat},${position.lng}`}
                                                  target="_blank"
                                                  rel="noreferrer"
                                                  sx={{ minWidth: 0, p: 0, fontSize: "0.72rem" }}
                                                >
                                                  {position.lat.toFixed(6)}, {position.lng.toFixed(6)}
                                                </Button>
                                              ) : "-"}
                                            </TableCell>
                                          </TableRow>
                                        );
                                      })
                                    ) : (
                                      units.map((id) => (
                                        <TableRow key={id}>
                                          <TableCell>{id}</TableCell>
                                          <TableCell colSpan={4} sx={{ color: "text.secondary" }}>
                                            Sin detalle disponible.
                                          </TableCell>
                                        </TableRow>
                                      ))
                                    )}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Pagination size="small" count={totalPages} page={page} onChange={(_, p) => setPage(p)} />
          </Box>
        )}

        <GrupoFormDialog
          open={formDialog.open}
          mode={formDialog.mode}
          grupo={formDialog.grupo}
          onClose={closeFormDialog}
          onSaved={handleFormDialogSaved}
          createGroup={createGroup}
          updateGroup={updateGroup}
          listUsers={listUsers}
          listUnits={listUnits}
          providerColor={providerColor}
        />

        <CentroEquipoDetalleDialog
          open={equipoDialog.open}
          onClose={() => setEquipoDialog({ open: false, identificador: "" })}
          tipo="modem"
          identificador={equipoDialog.identificador}
          title="Detalle del modem"
        />

        <Dialog
          open={infoDialog.open}
          onClose={() => setInfoDialog({ open: false, grupo: null })}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle sx={{ py: 1.1, fontSize: "1rem", fontWeight: 900 }}>
            Informacion del grupo
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              {getGrupoNombre(infoDialog.grupo || {})} - {getGrupoId(infoDialog.grupo || {}) || "-"}
            </Typography>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 1, display: "grid", gap: 1 }}>
            <Paper variant="outlined" sx={{ p: 1 }}>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.4fr 0.8fr 0.8fr" }, gap: 1 }}>
                <Box>
                  <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>Grupo</Typography>
                  <Typography sx={{ fontSize: "0.9rem", fontWeight: 900 }}>{getGrupoNombre(infoDialog.grupo || {})}</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>Unidades actuales</Typography>
                  <Typography sx={{ fontSize: "0.9rem", fontWeight: 900 }}>{getGrupoUnitIds(infoDialog.grupo || {}).length}</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>Usuarios actuales</Typography>
                  <Typography sx={{ fontSize: "0.9rem", fontWeight: 900 }}>{getGrupoUsuarios(infoDialog.grupo || {}).length}</Typography>
                </Box>
              </Box>
              <Box sx={{ mt: 0.75, display: "flex", flexWrap: "wrap", gap: 0.4 }}>
                {(infoDialog.grupo?.unidades_detalle || []).slice(0, 12).map((unidad) => (
                  <Chip key={getUnidadId(unidad)} size="small" label={getUnidadNombre(unidad)} sx={{ height: 20, fontSize: "0.66rem" }} />
                ))}
                {(infoDialog.grupo?.unidades_detalle || []).length > 12 && (
                  <Chip size="small" label={`+${(infoDialog.grupo?.unidades_detalle || []).length - 12}`} sx={{ height: 20, fontSize: "0.66rem" }} />
                )}
              </Box>
            </Paper>

            <Paper variant="outlined" sx={{ overflow: "hidden" }}>
              <Box sx={{ px: 1, py: 0.75, display: "flex", alignItems: "center", gap: 1, borderBottom: "1px solid #e5e7eb" }}>
                <Typography sx={{ fontSize: "0.82rem", fontWeight: 900, flex: 1 }}>Historial</Typography>
                {historyLoading && <CircularProgress size={16} />}
              </Box>
              <TableContainer sx={{ maxHeight: 430 }}>
                <Table size="small" stickyHeader sx={{ "& .MuiTableCell-root": { fontSize: "0.72rem", py: 0.5, px: 0.7 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ bgcolor: tableHeadBg, fontWeight: 900 }}>Fecha</TableCell>
                      <TableCell sx={{ bgcolor: tableHeadBg, fontWeight: 900 }}>Accion</TableCell>
                      <TableCell sx={{ bgcolor: tableHeadBg, fontWeight: 900 }}>Usuario</TableCell>
                      <TableCell sx={{ bgcolor: tableHeadBg, fontWeight: 900 }}>Cambios</TableCell>
                      <TableCell sx={{ bgcolor: tableHeadBg, fontWeight: 900 }}>Observacion</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {historyRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ color: "text.secondary", fontStyle: "italic" }}>
                          Sin historial registrado.
                        </TableCell>
                      </TableRow>
                    ) : historyRows.map((row) => (
                      <TableRow key={row.id_historial} hover>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDateTime(row.fecha_registro)}</TableCell>
                        <TableCell><Chip size="small" label={row.accion || "-"} sx={{ height: 20, fontSize: "0.66rem" }} /></TableCell>
                        <TableCell>{row.usuario || "-"}</TableCell>
                        <TableCell>{renderHistorySummary(row)}</TableCell>
                        <TableCell>{row.observacion || "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {historyTotalPages > 1 && (
                <Box sx={{ p: 0.75, display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1, borderTop: "1px solid #e5e7eb" }}>
                  <Button size="small" disabled={historyLoading || historyPage <= 1} onClick={() => handleHistoryPageChange(historyPage - 1)}>
                    Anterior
                  </Button>
                  <Typography sx={{ fontSize: "0.74rem", color: "text.secondary" }}>{historyPage} / {historyTotalPages}</Typography>
                  <Button size="small" disabled={historyLoading || historyPage >= historyTotalPages} onClick={() => handleHistoryPageChange(historyPage + 1)}>
                    Siguiente
                  </Button>
                </Box>
              )}
            </Paper>
          </DialogContent>
          <DialogActions sx={{ p: 0.75 }}>
            <Button size="small" onClick={() => setInfoDialog({ open: false, grupo: null })}>
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
    </DashboardLayout>
  );
}
